import React, { useEffect, useState } from "react";
import JoditEditor from "jodit-react";
import { Button, Form, message, Popconfirm } from "antd";
import { useParams } from "react-router-dom";
import { editArticleContent, getArticleByID } from "../action";
import { MdEdit } from "react-icons/md";

interface FormData {
  content: string;
}

type Props = {
  onContentChange: () => void;
};

const EditContent = (props: Props) => {
  const { onContentChange } = props;
  const [form] = Form.useForm<FormData>();
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>(false);

  // Load Cairo font once globally
  useEffect(() => {
    const href =
      "https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;600;700&display=swap";
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    }
  }, []);

  const fetchData = async () => {
    if (!id) return;
    await getArticleByID(id).then((article) => {
      if (article) {
        form.setFieldsValue({
          content: article.Content,
        });
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const onSubmit = async (values: FormData) => {
    setLoading(true);
    try {
      await editArticleContent(id!, values.content).then((article) => {
        if (article) {
          message.success("Article content updated successfully");
          onContentChange?.();
        }
      });
    } catch (error) {
      message.error("Error updating article content");
    } finally {
      setLoading(false);
    }
  };

  const joditConfig = {
    readonly: false,
    height: "50vh",
    placeholder: "Write long content in ...",
    style: {
      fontFamily: "'Cairo', sans-serif",
    },
    // Make Cairo available in dropdown
    font: {
      Cairo: "Cairo, sans-serif",
      Arial: "Arial, Helvetica, sans-serif",
      "Times New Roman": "Times New Roman, Times, serif",
    },
    defaultFont: "Cairo", // Set Cairo as default font in toolbar
    defaultStyle: {
      fontFamily: "'Cairo', sans-serif",
      fontSize: "16px",
    },
    // Ensures Cairo applies inside Jodit iframe if used
    iframeCSSLinks: [
      "https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;600;700&display=swap",
    ],
    iframeStyle:
      "body { font-family: 'Cairo', sans-serif; font-size: 16px; } p { margin: 0; }",
  };

  return (
    <Form layout="vertical" form={form} onFinish={onSubmit}>
      <Form.Item name="content" label="Content">
        <JoditEditor
          value={form.getFieldValue("content")}
          onChange={(value) => {
            form.setFieldValue("content", value);
          }}
          config={joditConfig}
        />
      </Form.Item>
      <Popconfirm
        title="Edit Article Content"
        description="Are you sure you want to edit this article's content?"
        onConfirm={() => form.submit()}
        okText="Yes"
        cancelText="No"
      >
        <Button type="primary" icon={<MdEdit />} loading={loading}>
          Edit Article Content
        </Button>
      </Popconfirm>
    </Form>
  );
};

export default EditContent;
